import i18n from "../i18n/i18n";
import {
  saveLocalTranslations,
  setCurrentLanguage,
  shouldUpdateTranslations,
  clearTranslationsCache,
  getLocalTranslations,
  getCurrentLanguage,
} from "../i18n/localStorage";

import enTranslations from "../locales/en.json";
import ruTranslations from "../locales/ru.json";
import kkTranslations from "../locales/kz.json";
import cnTranslations from "../locales/cn.json";
import { Localization } from "../services";

const localTranslations = {
  kz: { translation: kkTranslations },
  en: { translation: enTranslations },
  ru: { translation: ruTranslations },
  cn: { translation: cnTranslations },
};

export const applyLocalTranslations = () => {
  const langs = Object.keys(localTranslations);
  langs.forEach((lang) => {
    i18n.addResourceBundle(
      lang,
      "translation",
      localTranslations[lang].translation,
      true,
      true
    );
  });
  
  const currentLang = getCurrentLanguage();
  if (currentLang) {
    i18n.changeLanguage(currentLang);
  }
};

export const fetchTranslationByLang = async (lang) => {
  try {
    const response = await Localization.getLocalizationByLang(lang);

    if (!response?.data?.content) {
      throw new Error(`Нет перевода для языка ${lang}`);
    }

    const translation = JSON.parse(response.data.content);

    i18n.addResourceBundle(lang, "translation", translation, true, true);

    const existingTranslations = getLocalTranslations() || {};
    const updatedTranslations = {
      ...existingTranslations,
      [lang]: {
        translation,
      },
    };
    saveLocalTranslations(updatedTranslations);

    console.log(`Перевод для ${lang} успешно загружен с бэка`);
    return translation;
  } catch (error) {
    console.error(`Ошибка при получении перевода для ${lang}:`, error);
    
    i18n.addResourceBundle(
      lang, 
      "translation", 
      localTranslations[lang].translation, 
      true, 
      true
    );
    
    throw error;
  }
};

export const loadAllTranslations = async (forceUseLocal = false) => {
  try {
    applyLocalTranslations();
    
    const cachedTranslations = getLocalTranslations();

    if (!shouldUpdateTranslations() && !forceUseLocal && cachedTranslations) {
      console.info("Используем кешированные переводы из localStorage");

      Object.keys(cachedTranslations).forEach((lang) => {
        i18n.addResourceBundle(
          lang,
          "translation",
          cachedTranslations[lang].translation,
          true,
          true
        );
      });

      return cachedTranslations;
    }

    let resources = {};
    let fetchedFromBackend = false;

    if (!forceUseLocal) {
      const langs = Object.keys(localTranslations);
      const promises = langs.map(async (lang) => {
        try {
          const response = await Localization.getLocalizationByLang(lang);

          if (!response?.data?.content) {
            throw new Error(`Нет данных для языка ${lang}`);
          }

          try {
            const parsed = JSON.parse(response.data.content);
            resources[lang] = { translation: parsed };
            
            i18n.addResourceBundle(
              lang,
              "translation",
              parsed,
              true,
              true
            );
            
            fetchedFromBackend = true;
            console.log(`Перевод для ${lang} успешно загружен с бэка`);
          } catch (parseError) {
            console.error(`Ошибка при парсинге JSON для ${lang}:`, parseError);
            resources[lang] = localTranslations[lang];
          }
        } catch (error) {
          console.warn(
            `Не удалось получить перевод ${lang}, используем локальный`,
            error
          );
          resources[lang] = localTranslations[lang];
        }
      });

      try {
        await Promise.all(promises);
        
        if (fetchedFromBackend) {
          console.info("Переводы обновлены из бэкенда и сохранены в localStorage");
          saveLocalTranslations(resources);
        }
      } catch (error) {
        console.error("Ошибка при загрузке переводов:", error);
      }
    } else {
      console.info("Используем локальные переводы (forceUseLocal = true)");
      resources = { ...localTranslations };
    }

    return resources;
  } catch (error) {
    console.error("Ошибка при загрузке всех переводов:", error);
    
    applyLocalTranslations();
    
    throw error;
  }
};

export const changeLanguage = async (language) => {
  try {
    await i18n.changeLanguage(language);
    setCurrentLanguage(language);
  } catch (error) {
    console.error(`Ошибка при изменении языка на ${language}:`, error);
    throw error;
  }
};

export const refreshTranslations = async () => {
  clearTranslationsCache();
  return await loadAllTranslations();
};


// Отправка всех локалей на сервер
export const uploadAllLocalizations = async () => {
  const langs = Object.keys(localTranslations);

  for (const lang of langs) {
    const content = JSON.stringify(localTranslations[lang].translation);

    const data = {
      // version: '1.0.0',
      // lang,
      // content,
    };

    try {
      const response = await Localization.createLocalization(data);
      console.log(`✅ Успешно отправлен перевод для языка ${lang}`, response.data);
    } catch (error) {
      console.error(`❌ Ошибка при отправке перевода для языка ${lang}:`, error);
    }
  }
};

// Обновление всех локалей на сервере из локальных файлов
export const updateAllLocalizations = async () => {
  const localizationIds = {
    kz: "6808b673ef362c50fa46772e",
    en: "6808b674ef362c50fa46772f",
    ru: "6808b675ef362c50fa467731",
    cn: "6808b675ef362c50fa467733",
  };

  const langs = Object.keys(localTranslations);

  for (const lang of langs) {
    const localizationId = localizationIds[lang];

    if (!localizationId) {
      console.warn(`Нет ID для языка ${lang}, пропускаем`);
      continue;
    }

    const content = JSON.stringify(localTranslations[lang].translation);

    const data = {
      // version: '1.0.3',
      // lang,
      // content,
    };

    try {
      const response = await Localization.updateLocalization(localizationId, data);
      console.log(`Перевод для языка ${lang} успешно обновлён`, response.data);
    } catch (error) {
      console.error(`Ошибка при обновлении перевода для языка ${lang}:`, error);
    }
  }
};
